import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import HeroSection from './components/HeroSection';
import PortalSection from './components/PortalSection';
import CountdownSection from './components/CountdownSection';
import ParticleBackground from './components/ParticleBackground';

const AppContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
`;

const GradientOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(5, 5, 5, 0) 0%, rgba(5, 5, 5, 0.8) 80%, rgba(5, 5, 5, 1) 100%);
  pointer-events: none;
  z-index: 1;
`;

const FloatingElements = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
`;

const FloatingElement = styled(motion.div)<{ size: number }>`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(0, 191, 255, 0.2), rgba(136, 0, 255, 0.2));
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  box-shadow: 0 0 20px rgba(0, 191, 255, 0.5);
  backdrop-filter: blur(2px);
`;

const App: React.FC = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const floatingElementsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!appRef.current || !floatingElementsRef.current) return;

    // Create floating elements
    const createFloatingElements = () => {
      const container = floatingElementsRef.current;
      if (!container) return;

      // Clear previous elements
      container.innerHTML = '';

      // Create new elements
      for (let i = 0; i < 5; i++) {
        const element = document.createElement('div');
        const size = Math.random() * 100 + 50;
        
        element.style.position = 'absolute';
        element.style.width = `${size}px`;
        element.style.height = `${size}px`;
        element.style.borderRadius = '50%';
        element.style.background = `linear-gradient(45deg, 
          rgba(${Math.random() * 255}, ${Math.random() * 100}, 255, 0.1), 
          rgba(255, ${Math.random() * 100}, 255, 0.1))`;
        element.style.boxShadow = '0 0 30px rgba(0, 191, 255, 0.3)';
        element.style.backdropFilter = 'blur(3px)';
        
        // Random positions
        element.style.left = `${Math.random() * 100}%`;
        element.style.top = `${Math.random() * 100}%`;
        
        container.appendChild(element);
        
        // Animate with GSAP
        gsap.to(element, {
          x: Math.random() * 200 - 100,
          y: Math.random() * 200 - 100,
          rotation: Math.random() * 360,
          duration: Math.random() * 20 + 10,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      }
    };

    createFloatingElements();
    
    // Handle resize
    const handleResize = () => {
      createFloatingElements();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <AppContainer ref={appRef}>
      <ParticleBackground />
      <FloatingElements ref={floatingElementsRef} />
      <GradientOverlay />
      
      <HeroSection />
      <PortalSection />
      <CountdownSection />
    </AppContainer>
  );
};

export default App; 